import * as THREE from 'three';
import { PointSizeType } from '../defines';
import { EventDispatcher } from '../EventDispatcher';
import { Utils } from '../utils';
import { BoxVolume } from './Volume';

/** jQuery-like element */
interface JQueryElement {
  css(prop: string, value: string): JQueryElement;
  remove(): void;
  append(el: JQueryElement): JQueryElement;
}

/** jQuery-like global */
declare const $: (selector: string | HTMLElement | null) => JQueryElement;

/** Viewer interface for ScreenBoxSelectTool */
interface ScreenBoxViewer {
  renderer: THREE.WebGLRenderer;
  scene: {
    getActiveCamera(): THREE.OrthographicCamera;
    addVolume(v: BoxVolume): void;
    pointclouds: Array<{
      visible: boolean;
      pick(viewer: ScreenBoxViewer, camera: THREE.Camera, ray: THREE.Ray, params: Record<string, unknown>): Array<{ position: THREE.Vector3 }>;
    }>;
  };
  inputHandler: {
    deselectAll(): void;
    toggleSelection(v: BoxVolume): void;
    addInputListener(listener: EventDispatcher): void;
  };
  addEventListener(event: string, handler: (...args: unknown[]) => void): void;
}

/** Drag event data */
interface DragEvent {
  drag: {
    start: THREE.Vector2;
    end: THREE.Vector2;
  };
  viewer: ScreenBoxViewer;
  consume(): void;
}

export class ScreenBoxSelectTool extends EventDispatcher {
  viewer: ScreenBoxViewer;
  scene: THREE.Scene;
  importance: number;

  constructor(viewer: ScreenBoxViewer) {
    super();

    this.viewer = viewer;
    this.scene = new THREE.Scene();
    this.importance = 0;

    viewer.addEventListener('update', this.update.bind(this));
    viewer.addEventListener('render.pass.perspective_overlay', this.render.bind(this));
    viewer.addEventListener('scene_changed', this.onSceneChange.bind(this));
  }

  onSceneChange(_scene: unknown) {
    // scene changed handler
  }

  startInsertion() {
    const domElement = this.viewer.renderer.domElement;

    const volume = new BoxVolume();
    volume.position.set(12345, 12345, 12345);
    volume.showVolumeLabel = false;
    volume.visible = false;
    volume.update();
    this.viewer.scene.addVolume(volume);

    this.importance = 10;

    const selectionBox = $(`<div style="position: absolute; border: 2px solid white; pointer-events: none; border-style:dashed"></div>`);
    $(domElement.parentElement).append(selectionBox);
    selectionBox.css('right', '10px');
    selectionBox.css('bottom', '10px');

    const drag = (e: DragEvent) => {
      volume.visible = true;

      const mStart = e.drag.start;
      const mEnd = e.drag.end;

      const box2D = new THREE.Box2();
      box2D.expandByPoint(mStart);
      box2D.expandByPoint(mEnd);

      selectionBox.css('left', `${box2D.min.x}px`);
      selectionBox.css('top', `${box2D.min.y}px`);
      selectionBox.css('width', `${box2D.max.x - box2D.min.x}px`);
      selectionBox.css('height', `${box2D.max.y - box2D.min.y}px`);

      const camera = e.viewer.scene.getActiveCamera();
      const size = e.viewer.renderer.getSize(new THREE.Vector2());
      const frustumSize = new THREE.Vector2(camera.right - camera.left, camera.top - camera.bottom);

      const screenCentroid = new THREE.Vector2().addVectors(e.drag.end, e.drag.start).multiplyScalar(0.5);
      const ray = Utils.mouseToRay(screenCentroid, camera, size.width, size.height);

      const diff = new THREE.Vector2().subVectors(e.drag.end, e.drag.start);
      diff.divide(size).multiply(frustumSize);

      volume.position.copy(ray.origin);
      volume.up.copy(camera.up);
      volume.rotation.copy(camera.rotation);
      volume.scale.set(diff.x, diff.y, 1000 * 100);

      e.consume();
    };

    const drop = (e: DragEvent) => {
      this.importance = 0;

      selectionBox.remove();

      this.viewer.inputHandler.deselectAll();
      this.viewer.inputHandler.toggleSelection(volume);

      const camera = e.viewer.scene.getActiveCamera();
      const size = e.viewer.renderer.getSize(new THREE.Vector2());
      const screenCentroid = new THREE.Vector2().addVectors(e.drag.end, e.drag.start).multiplyScalar(0.5);
      const ray = Utils.mouseToRay(screenCentroid, camera, size.width, size.height);

      this.removeEventListener('drag', drag as (...args: unknown[]) => void);
      this.removeEventListener('drop', drop as (...args: unknown[]) => void);

      const allPointsNear: Array<{ position: THREE.Vector3 }> = [];
      const allPointsFar: Array<{ position: THREE.Vector3 }> = [];

      // TODO support more than one point cloud
      for (const pointcloud of this.viewer.scene.pointclouds) {
        if (!pointcloud.visible) {
          continue;
        }

        const volCam = camera.clone();
        volCam.left = -volume.scale.x / 2;
        volCam.right = +volume.scale.x / 2;
        volCam.top = +volume.scale.y / 2;
        volCam.bottom = -volume.scale.y / 2;
        volCam.near = -volume.scale.z / 2;
        volCam.far = +volume.scale.z / 2;
        volCam.rotation.copy(volume.rotation);
        volCam.position.copy(volume.position);

        volCam.updateMatrix();
        volCam.updateMatrixWorld();
        volCam.updateProjectionMatrix();
        volCam.matrixWorldInverse.copy(volCam.matrixWorld).invert();

        const pickRay = new THREE.Ray(volCam.getWorldPosition(new THREE.Vector3()), volCam.getWorldDirection(new THREE.Vector3()));
        const rayInverse = new THREE.Ray(pickRay.origin.clone().add(pickRay.direction.clone().multiplyScalar(volume.scale.z)), pickRay.direction.clone().multiplyScalar(-1));

        const pickerSettings = {
          width: 8,
          height: 8,
          pickWindowSize: 8,
          all: true,
          pickClipped: false,
          pointSizeType: PointSizeType.FIXED,
          pointSize: 1,
        };
        const pointsNear = pointcloud.pick(this.viewer, volCam, pickRay, pickerSettings);

        volCam.rotateX(Math.PI);
        volCam.updateMatrix();
        volCam.updateMatrixWorld();
        volCam.updateProjectionMatrix();
        volCam.matrixWorldInverse.copy(volCam.matrixWorld).invert();
        const pointsFar = pointcloud.pick(this.viewer, volCam, rayInverse, pickerSettings);

        allPointsNear.push(...pointsNear);
        allPointsFar.push(...pointsFar);
      }

      if (allPointsNear.length > 0 && allPointsFar.length > 0) {
        const viewLine = new THREE.Line3(ray.origin, new THREE.Vector3().addVectors(ray.origin, ray.direction));

        const closestOnLine = allPointsNear.map((p) => viewLine.closestPointToPoint(p.position, false, new THREE.Vector3()));
        const closest = closestOnLine.sort((a, b) => ray.origin.distanceTo(a) - ray.origin.distanceTo(b))[0];

        const farthestOnLine = allPointsFar.map((p) => viewLine.closestPointToPoint(p.position, false, new THREE.Vector3()));
        const farthest = farthestOnLine.sort((a, b) => ray.origin.distanceTo(b) - ray.origin.distanceTo(a))[0];

        const distance = closest.distanceTo(farthest);
        const centroid = new THREE.Vector3().addVectors(closest, farthest).multiplyScalar(0.5);
        volume.scale.z = distance * 1.1;
        volume.position.copy(centroid);
      }

      volume.clip = true;
    };

    this.addEventListener('drag', drag as (...args: unknown[]) => void);
    this.addEventListener('drop', drop as (...args: unknown[]) => void);

    this.viewer.inputHandler.addInputListener(this);

    return volume;
  }

  update(_e: unknown) {
    //console.log(e.delta)
  }

  render() {
    this.viewer.renderer.render(this.scene, this.viewer.scene.getActiveCamera());
  }
}
